import { DragDropModule } from '@angular/cdk/drag-drop';
import { LayoutModule } from '@angular/cdk/layout';
import {
    CommonModule,
    DatePipe,
    DecimalPipe,
    LocationStrategy,
    PathLocationStrategy,
    PercentPipe
} from '@angular/common';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import {
    APP_INITIALIZER,
    CUSTOM_ELEMENTS_SCHEMA,
    FactoryProvider,
    LOCALE_ID,
    NgModule,
    ErrorHandler
} from '@angular/core';
import {
    ActivatedRouteSnapshot,
    BaseRouteReuseStrategy,
    Router,
    RouteReuseStrategy
} from '@angular/router';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyChipsModule as MatChipsModule } from '@angular/material/legacy-chips';
import {
    MatLegacyDialog as MatDialog,
    MatLegacyDialogModule as MatDialogModule
} from '@angular/material/legacy-dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyListModule as MatListModule } from '@angular/material/legacy-list';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { LandingPageResolver } from '@resolvers';
import { SbEventBusService, SbInterceptorInterceptor, SbMenuService } from '@services';
import * as Sentry from '@sentry/angular';
import {
    SbAdvancedCellComponent,
    SbAgTableComponent,
    SbAgDetailsRenderer,
    SbFormWidgetComponent,
    SbKeyFigureAdvancedComponent,
    SbLinechartComponent,
    SbMultiSelectComponent,
    SbTablePieComponent,
    SbWidgetConfigDialogComponent
} from './legacy/widgets';
import { CardWrapperComponent } from '@wrappers';
import { AgGridModule } from 'ag-grid-angular';
import { TextMaskModule } from 'angular2-text-mask';
import { AppComponent } from './app.component';
import { MaterialModule } from './legacy/material/material.module';
import { PageNotFoundComponent } from './pages/public/page-not-found/page-not-found.component';
import { SbConfirmDialogComponent } from './legacy/sb-confirm-dialog/sb-confirm-dialog.component';
import { SbDialogComponent } from './legacy/sb-dialog/sb-dialog.component';
import { SbMultiselectCheckAllComponent } from './legacy/sb-dynamic-form/field-types/sb-multiselect-check-all/sb-multiselect-check-all.component';
import { FORMLY_COMPONENTS, FORMLY_MODULE } from './legacy/sb-dynamic-form/FormlyConfig';
import { SbForgotPasswordComponent } from './pages/public/sb-forgot-password/sb-forgot-password.component';
import { SbGridComponent } from './legacy/sb-grid/sb-grid.component';
import { SbPrintLayoutComponent } from './legacy/sb-grid/sb-print-layout/sb-print-layout.component';
import { SbMessagesComponent } from './legacy/sb-messages/sb-messages.component';
import { SbConnectionOverlayComponent } from './legacy/sb-overlay/sb-connection-overlay.component';
import { SbPanelComponent } from './legacy/sb-panel/sb-panel.component';
import { SbResetPasswordComponent } from './pages/public/sb-reset-password/sb-reset-password.component';
import { SbSnackbarComponent } from './legacy/sb-snackbar/sb-snackbar.component';
import { SbUploadWithPreviewComponent } from './legacy/sb-upload-with-preview/sb-upload-with-preview.component';
import { SbUploadWithPreviewDirective } from './legacy/sb-upload-with-preview/sb-upload-with-preview.directive';
import { FileUploadComponent } from './legacy/sb-upload/file-upload-component/file-upload.component';
import { SbWidgetDirective } from './sb-widget.directive';
import { BaseWidgetComponent } from './legacy/widgets/base-widget/base-widget.component';
import { KeyFigureBasicComponent } from './legacy/widgets/key-figure-basic/key-figure-basic.component';
import { SbWidgetButtonBarComponent } from './legacy/widgets/sb-widget-button-bar/sb-widget-button-bar.component';
import { SbKeyFigureToFlatmodelPipe } from './legacy/widgets/sb-key-figure-advanced/pipes/sb-key-figure-to-flatmodel.pipe';
import { AppRoutingModule } from './app-routing.module';
import { SbSelectComponent } from './legacy/widgets';
import { CoreModule } from './core/core.module';
import { AkitaNgDevtools } from '@datorama/akita-ngdevtools';
import { AkitaNgRouterStoreModule } from '@datorama/akita-ng-router-store';
import { environment } from '@environment';
import { AuthenticationInterceptor } from './core/interceptors/authentication.interceptor';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SbAgTableButtonRenderer } from './legacy/widgets/sb-ag-table/sb-ag-table-button-renderer/sb-ag-table-button-renderer.component';
import { RetryInterceptor } from './core/interceptors/retry.interceptor';
import { PasswordValidationModule } from './legacy/password-validation/password-validation.module';
import { SbAgTableLinkRendererComponent } from './legacy/widgets/sb-ag-table/sb-ag-table-link-renderer/sb-ag-table-link-renderer.component';
import { AppConfigService } from './core/state/app-config/app-config.service';
import { AppConfigQuery } from './core/state/app-config/app-config.query';
import { combineLatest } from 'rxjs';
import { TranslateEnumPipe } from './shared/pipes';
import { LoginComponent } from './pages/public/login/login.component';
import { IconsModule } from './shared/icons/icons.module';
import {
    KeycloakAngularModule,
    KeycloakEventType,
    KeycloakService
} from 'keycloak-angular';
import { switchMap } from 'rxjs/operators';
import { DateRelativePipe } from './shared/pipes/date-relative.pipe';
import { SbContainerSpacerDirective } from './shared/directives/sb-container-spacer.directive';
import { SbPeriodPickerTempComponent } from './shared/components/form/sb-period-picker-temp/sb-period-picker-temp.component';
import { SbAccountFilterSelectComponent } from './shared/components/form/sb-account-filter-select/sb-account-filter-select.component';
import { SbBadgesComponent } from './shared/components/sb-badges/sb-badges.component';
import { BlobErrorInterceptor } from './core/interceptors/blob-error.interceptor';
import { SbDisclaimerComponent } from './shared/components/sb-disclamer/sb-disclaimer.component';
import { FormatDeciderPipe } from './shared/pipes/format-decider.pipe';
import { AbbreviateNumberPipe } from './shared/pipes/abbreviate-number.pipe';
import { SessionStore } from './core/state/session/session.store';
import { PortfolioReportFilterPipe } from './shared/pipes/portfolio-report-filter.pipe';
import { SbAccountListComponent } from './shared/components/sb-account-list/sb-account-list.component';
import { CountryCodePipe } from './shared/pipes/country-code.pipe';
import { SbCompanyLogoComponent } from './shared/components/sb-company-logo/sb-company-logo.component';
import { SbAnimatedNumberComponent } from './shared/components/sb-animated-number/sb-animated-number.component';
import { SbGroupAccountsPipe } from './legacy/widgets/sb-key-figure-advanced/pipes/sb-group-accounts.pipe';
import { SbTopHoldingsComponent } from './shared/components/sb-top-holdings/sb-top-holdings.component';
import { SbTempPerformanceComponent } from './shared/components/sb-temp-performance/sb-temp-performance.component';
import { SbSubAccountsPipe } from './legacy/widgets/sb-key-figure-advanced/pipes/sb-sub-accounts.pipe';
import { deepEquals } from '@utils';
import { SbListSubAccountsComponent } from './legacy/widgets/sb-key-figure-advanced/components/sb-list-sub-accounts/sb-list-sub-accounts.component';
import { SbBalanceListComponent } from './pages/authenticated/overview/components/sb-balance-list/sb-balance-list.component';
import { GlobalErrorHandler } from './core/handlers/global-error.handler';

export const loadConfigProvider: FactoryProvider = {
    provide: APP_INITIALIZER,
    useFactory: loadAppConfigFactory,
    deps: [
        AppConfigService,
        AppConfigQuery,
        KeycloakService,
        SessionStore,
        Sentry.TraceService
    ],
    multi: true
};

class CustomRouteReuseStrategy extends BaseRouteReuseStrategy {
    shouldReuseRoute(
        future: ActivatedRouteSnapshot,
        curr: ActivatedRouteSnapshot
    ): boolean {
        // reload component on same route navigation if path starts with any of the paths in pathsToReload
        const pathsToReload = ['/sb-grid/portfolio-summary'];
        const url = `/${future.url.map((segment) => segment.path).join('/')}`;
        const shouldReload = pathsToReload.some((path) => url.startsWith(path));
        return shouldReload
            ? !shouldReload &&
                  future.routeConfig === curr.routeConfig &&
                  deepEquals(future.params, curr.params)
            : future.routeConfig === curr.routeConfig &&
                  deepEquals(future.params, curr.params);
    }
}

@NgModule({
    imports: [
        BrowserModule,
        KeycloakAngularModule,
        MaterialModule,
        CommonModule,
        FormsModule,
        AppRoutingModule,
        HttpClientModule,
        BrowserAnimationsModule,
        DragDropModule,
        AgGridModule.withComponents([SbAgTableButtonRenderer]),
        ReactiveFormsModule,
        FORMLY_MODULE,
        FormlyMaterialModule,
        TextMaskModule,
        LayoutModule,
        MatToolbarModule,
        MatButtonModule,
        MatSidenavModule,
        MatIconModule,
        MatListModule,
        MatMenuModule,
        MatDialogModule,
        MatExpansionModule,
        MatChipsModule,
        //PrivateAssetModule,
        //RealEstateModule,
        CoreModule,
        environment.production ? [] : AkitaNgDevtools.forRoot(),
        AkitaNgRouterStoreModule,
        PasswordValidationModule,
        IconsModule,
        SbPeriodPickerTempComponent,
        SbAccountFilterSelectComponent,
        SbBadgesComponent,
        DateRelativePipe,
        SbContainerSpacerDirective,
        SbDisclaimerComponent,
        SbContainerSpacerDirective,
        IconsModule,
        SbBadgesComponent,
        DateRelativePipe,
        SbDisclaimerComponent,
        SbBadgesComponent,
        FormatDeciderPipe,
        AbbreviateNumberPipe,
        SbAccountListComponent,
        CountryCodePipe,
        SbCompanyLogoComponent,
        SbAnimatedNumberComponent,
        SbGroupAccountsPipe,
        SbTopHoldingsComponent,
        SbTempPerformanceComponent,
        SbSubAccountsPipe,
        SbListSubAccountsComponent,
        SbBalanceListComponent
    ],
    declarations: [
        AppComponent,
        LoginComponent,
        SbGridComponent,
        KeyFigureBasicComponent,
        SbLinechartComponent,
        SbPanelComponent,
        SbWidgetDirective,
        SbAgTableComponent,
        SbAgTableLinkRendererComponent,
        SbKeyFigureAdvancedComponent,
        SbTablePieComponent,
        SbAdvancedCellComponent,
        SbMultiSelectComponent,
        SbForgotPasswordComponent,
        SbResetPasswordComponent,
        PageNotFoundComponent,
        CardWrapperComponent,
        FileUploadComponent,
        ...FORMLY_COMPONENTS,
        SbFormWidgetComponent,
        BaseWidgetComponent,
        SbConnectionOverlayComponent,
        SbUploadWithPreviewComponent,
        SbConfirmDialogComponent,
        SbMessagesComponent,
        SbDialogComponent,
        SbSnackbarComponent,
        SbUploadWithPreviewDirective,
        SbMultiselectCheckAllComponent,
        SbPrintLayoutComponent,
        PortfolioReportFilterPipe,
        SbWidgetConfigDialogComponent,
        SbWidgetButtonBarComponent,
        SbKeyFigureToFlatmodelPipe,
        SbSelectComponent,
        SbAgDetailsRenderer,
        SbAgTableButtonRenderer
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    providers: [
        { provide: ErrorHandler, useClass: GlobalErrorHandler },
        {
            provide: Sentry.TraceService,
            deps: [Router]
        },
        { provide: LOCALE_ID, useValue: 'sv' },
        loadConfigProvider,
        // Experimental: Remove HashLocationStrategy
        //        { provide: LocationStrategy, useClass: HashLocationStrategy },
        { provide: LocationStrategy, useClass: PathLocationStrategy },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: RetryInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: AuthenticationInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SbInterceptorInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: BlobErrorInterceptor,
            multi: true
        },
        {
            provide: RouteReuseStrategy,
            useClass: CustomRouteReuseStrategy
        },
        DatePipe,
        DecimalPipe,
        PercentPipe,
        SbEventBusService,
        SbMenuService,
        MatDialog,
        LandingPageResolver,
        SbKeyFigureToFlatmodelPipe,
        TranslateEnumPipe,
        KeycloakService,
        AbbreviateNumberPipe
    ],
    bootstrap: [AppComponent]
})
export class AppModule {}

function loadAppConfigFactory(
    appConfigService: AppConfigService,
    appConfigQuery: AppConfigQuery,
    keycloak: KeycloakService,
    sessionStore: SessionStore
) {
    // bootstrap app when config has been received and app config state has been set
    return () => {
        /* on successful keycloak auth, populate session sotre */
        keycloak.keycloakEvents$.subscribe({
            next(event) {
                const instance = keycloak.getKeycloakInstance();
                if (
                    (event.type == KeycloakEventType.OnAuthSuccess ||
                        event.type === KeycloakEventType.OnAuthRefreshSuccess) &&
                    sessionStore
                ) {
                    sessionStore.update((state) => ({
                        ...state,
                        access_token: instance.token,
                        id_token: instance.idToken,
                        refresh_token: instance.refreshToken,
                        loadingState: 'success',
                        token_type: instance.tokenParsed['typ'],
                        scope: instance.tokenParsed['scope']
                    }));
                }
            }
        });

        return combineLatest([appConfigService.get(), appConfigQuery.hasLoaded$]).pipe(
            switchMap(([config, hasLoaded]) => {
                if (hasLoaded) {
                    const { keycloakUrl, keycloakRealm, useLatestKeycloakFlow } =
                        appConfigQuery.get();

                    const environment = config.environment;

                    Sentry.init({
                        dsn: 'https://a7a78feb2ab238046c0470c083ad3476@o4507383637147648.ingest.de.sentry.io/4507383693836368',
                        environment: environment,
                        integrations: [
                            Sentry.browserTracingIntegration(),
                            Sentry.replayIntegration({
                                maskAllText: false,
                                blockAllMedia: false
                            }),
                            Sentry.browserProfilingIntegration(),
                            Sentry.httpClientIntegration(),
                            Sentry.sessionTimingIntegration()
                        ],
                        // Performance Monitoring
                        tracesSampleRate: 1.0, //  Capture 100% of the transactions
                        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
                        tracePropagationTargets: [
                            'localhost',
                            /^https:\/\/seb-poc-test\.swimbird\.se\/.*/,
                            /^https:\/\/seb-poc-stage\.swimbird\.se\/.*/,
                            /^https:\/\/seb-poc\.swimbird\.se\/.*/
                        ],
                        // Session Replay
                        replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
                        replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
                        enabled: environment !== 'development' // Disable Sentry in development
                    });

                    const kc = keycloak
                        .init({
                            config: {
                                url: keycloakUrl,
                                realm: keycloakRealm,
                                clientId: 'swip'
                            },
                            initOptions: {
                                onLoad: 'check-sso',
                                // Disabled because of Safari issue with this lib version
                                // silentCheckSsoRedirectUri: environment.production
                                //     ? `${window.location.origin}/${
                                //           appConfigQuery.get().keycloakRealm
                                //       }/assets/silent-check-sso.html`
                                //     : `${window.location.origin}/assets/silent-check-sso.html`,
                                pkceMethod: 'S256',
                                checkLoginIframe: false
                            },
                            loadUserProfileAtStartUp: true,
                            /* we are using custom session store for various features
                            and a custom authentication interceptor */
                            shouldAddToken: () => {
                                return false;
                            }
                        })
                        .catch((err) =>
                            console.info('[keycloak] Conf missing, using to legacy flow.')
                        );

                    if (!useLatestKeycloakFlow) return [config, hasLoaded];
                    else return kc;
                }
            })
        );
    };
}
